/**
 * Composite ZPL printer display names and stage-1 floor select labels.
 *
 * Stage 2 will replace the printer dropdown with location-only routing; until then
 * operators still pick a printer, and stored prefs fall back quietly when a rename
 * leaves a stale localStorage value.
 */

export type ZplPrinterListItem = {
  name: string;
  description?: string;
  location?: string;
  ip?: string;
  port?: number;
  dpi?: number;
  /** Normalized label stock size from the print relay when provided. */
  labelSize?: string;
  id?: string;
  aliases?: string[];
  displayName?: string;
  station?: string;
  stationId?: number;
  model?: string;
  identifier?: string;
  /** When set, the print server reported TCP reachability for this queue. */
  online?: boolean | null;
};

const LABEL_SIZE_IN_TEXT = /\b\d+(?:\.\d+)?x\d+(?:\.\d+)?\b/i;

function descriptionRedundantlyRepeatsName(name: string, description: string): boolean {
  const nameLower = name.toLowerCase();
  const descLower = description.toLowerCase();
  if (descLower === nameLower) return true;
  if (nameLower.includes(descLower) || descLower.includes(nameLower)) return true;

  const descCore = descLower
    .replace(/\([^)]*\)/g, " ")
    .replace(LABEL_SIZE_IN_TEXT, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (descCore === "") return true;
  if (nameLower.includes(descCore)) return true;

  const nameTokens = nameLower.split(/\s+/).filter(Boolean);
  const descTokens = descCore.split(/\s+/).filter(Boolean);
  if (nameTokens.length >= 2 && descTokens.length >= 2) {
    const namePrefix = nameTokens.slice(0, 2).join(" ");
    const descPrefix = descTokens.slice(0, 2).join(" ");
    if (namePrefix === descPrefix) return true;
  }
  return false;
}

function composeDisplayName(printer: ZplPrinterListItem): string | null {
  const fromApi = printer.displayName?.trim();
  if (fromApi != null && fromApi !== "") return fromApi;

  const station = printer.station?.trim() ?? "";
  const stock = printer.labelSize?.trim() ?? "";
  const model = printer.model?.trim() ?? "";
  const identifier = printer.identifier?.trim() ?? "";
  // Only use the composite when hub stage-1 fields are present; otherwise keep legacy labels.
  if (station === "" && model === "" && identifier === "") return null;
  const left = [station, stock].filter((part) => part !== "").join(" ");
  const paren = [model, identifier].filter((part) => part !== "").join(" ");
  if (left !== "" && paren !== "") return `${left} (${paren})`;
  if (left !== "") return left;
  if (paren !== "") return paren;
  return null;
}

/** Human-readable option label for the shared ZPL printer select. */
export function formatZplPrinterOptionLabel(printer: ZplPrinterListItem): string {
  const composed = composeDisplayName(printer);
  if (composed != null) return composed;

  const name = printer.name.trim();
  const labelSize = printer.labelSize?.trim();

  if (
    labelSize != null &&
    labelSize !== "" &&
    !LABEL_SIZE_IN_TEXT.test(name)
  ) {
    return `${name} · ${labelSize}`;
  }

  const description = printer.description?.trim();
  if (
    description != null &&
    description !== "" &&
    !descriptionRedundantlyRepeatsName(name, description)
  ) {
    return `${name} · ${description}`;
  }

  return name;
}

/** Whether a stored selection still addresses a printer in the current list. */
export function zplPrinterListIncludesRef(
  printers: readonly ZplPrinterListItem[],
  ref: string,
): boolean {
  const trimmed = ref.trim();
  if (trimmed === "") return false;
  return printers.some((printer) => {
    if (printer.id != null && printer.id.trim() === trimmed) return true;
    if (printer.name.trim() === trimmed) return true;
    return (printer.aliases ?? []).some((alias) => alias.trim() === trimmed);
  });
}
